import {
  MOMENTUM_DIMENSION_COPY,
  MOMENTUM_STATE_LABEL,
  MOMENTUM_DIRECTION_LABEL,
} from "./copy";
import { MOMENTUM_NOT_ENOUGH_DATA } from "../../Services/journey";
import { commitmentTiming, progressDay } from "./commitmentFormat";
import { routeForDeepLink } from "./deepLinks";
import openIcon from "../../Assets/icon/journey/commitment-open.png";
import doneIcon from "../../Assets/icon/journey/commitment-done.svg";
import chevronIcon from "../../Assets/icon/journey/chevron-right.svg";
import arrowUp from "../../Assets/icon/journey/arrow-up.svg";
import arrowRight from "../../Assets/icon/journey/arrow-right.svg";

/**
 * Blocks shared by the Journey home and its "see all" pages (Figma
 * 10905:20092, 10880:19687, 10911:20849).
 */

/** A Journey section's title + subtitle, drawn above its card. */
export const JourneySectionHead = ({ title, subtitle }) => (
  <div className="journey-section__head">
    <h2 className="journey-section__title">{title}</h2>
    {subtitle && <p className="journey-section__subtitle">{subtitle}</p>}
  </div>
);

/**
 * One commitment row: open / completed icon chosen by `is_open` (never by a
 * hardcoded list of states), `text`, and the timing line. No "2 of 3": a
 * commitment has one `due_at` and no occurrences (§7 J2).
 */
export const CommitmentRow = ({ commitment, onOpen }) => {
  const timing = commitmentTiming(commitment);
  return (
    <button
      type="button"
      className="journey-row journey-row--link"
      onClick={() => onOpen?.(commitment)}
    >
      <span className="journey-row__icon">
        {commitment.is_open ? (
          <img src={openIcon} alt="" width="25" height="25" />
        ) : (
          <img src={doneIcon} alt="" width="32" height="32" />
        )}
      </span>
      <span className="journey-row__text">
        <span className="journey-row__title">{commitment.text}</span>
        {timing && <span className="journey-row__sub">{timing}</span>}
      </span>
      <span className="journey-row__chevron">
        <img src={chevronIcon} alt="" width="16" height="16" />
      </span>
    </button>
  );
};

const DIRECTION_ICON = { IMPROVING: arrowUp, STEADY: arrowRight };

/**
 * Momentum v2 — always three dimensions, in the order sent. Each shows a
 * state label (with direction) OR, for NOT_ENOUGH_DATA, only its `why`; `why`
 * is shown under every state. No number, ring or bar anywhere.
 */
export const MomentumDimensions = ({ momentum }) => {
  const dims = Array.isArray(momentum?.dimensions) ? momentum.dimensions : [];
  if (dims.length === 0) return null;
  return (
    <div className="journey-momentum">
      {dims.map((dim) => {
        const copy = MOMENTUM_DIMENSION_COPY[dim.dimension];
        if (!copy) return null;
        const establishing = dim.state === MOMENTUM_NOT_ENOUGH_DATA;
        const stateLabel = establishing ? null : MOMENTUM_STATE_LABEL[dim.state];
        const direction = MOMENTUM_DIRECTION_LABEL[dim.direction];
        return (
          <section key={dim.dimension} className="journey-card journey-momentum__card">
            <div className="journey-momentum__head">
              <p className="journey-momentum__label">{copy.label}</p>
              <p className="journey-momentum__sub">{copy.subtitle}</p>
            </div>
            <div className="journey-momentum__body">
              {stateLabel && (
                <div className="journey-momentum__state">
                  <span className="journey-momentum__value">{stateLabel}</span>
                  {direction && (
                    <span
                      className={`journey-momentum__dir journey-momentum__dir--${String(
                        dim.direction
                      ).toLowerCase()}`}
                    >
                      {DIRECTION_ICON[dim.direction] && (
                        <img src={DIRECTION_ICON[dim.direction]} alt="" width="16" height="16" />
                      )}
                      {direction}
                    </span>
                  )}
                </div>
              )}
              {dim.why && <p className="journey-momentum__why">{dim.why}</p>}
            </div>
          </section>
        );
      })}
    </div>
  );
};

/**
 * A string the member can actually read. `title`/`summary` are encrypted at
 * rest; an `enc:v1:` envelope reaching the client is a server bug (guide
 * §8.1), so it is treated as absent rather than printed.
 */
const plain = (value) =>
  typeof value === "string" && value && !value.startsWith("enc:") ? value : "";

/**
 * Your Progress rows — life events only, newest first: date line, then the
 * event. A row is tappable only when its `deep_link` maps to a screen here;
 * `null` or an unknown target renders as plainly not tappable.
 */
export const ProgressRows = ({ events, onOpen }) =>
  (events || []).map((event, index) => {
    const route = routeForDeepLink(event?.deep_link);
    const body = plain(event?.summary) || plain(event?.title);
    if (!body) return null;
    const content = (
      <span className="journey-row__text">
        <span className="journey-row__eyebrow">{progressDay(event.occurred_at)}</span>
        <span className="journey-row__title">{body}</span>
      </span>
    );
    return route ? (
      <button
        type="button"
        key={event.id || index}
        className="journey-row journey-row--link"
        onClick={() => onOpen?.(route)}
      >
        {content}
        <span className="journey-row__chevron">
          <img src={chevronIcon} alt="" width="16" height="16" />
        </span>
      </button>
    ) : (
      <div key={event.id || index} className="journey-row">
        {content}
      </div>
    );
  });
